import React, { useState } from 'react'
import NumberFormat from 'react-number-format'
import { toast } from 'react-toastify'

import {
    Box,
    Typography,
    Grid,
    TextField,
    MenuItem,
    Button,
    Dialog,
    DialogActions,
} from '@material-ui/core'
import { KeyboardDatePicker } from '@material-ui/pickers'
import { useTheme } from '@material-ui/core/styles'

import { InternalInvoice, PaymentForm } from '../../../models/Invoice/types'
import { formatCurrency } from './helpers'
import { typographyStyle } from '../../../theme/typographyStyle'

const PAYMENT_METHODS = [
    ['CASH', 'Cash'],
    ['CHECK', 'Check'],
    ['CREDIT_CARD', 'Credit Card'],
    ['DEBIT_CARD', 'Debit Card'],
    ['ACH_TRANSFER', 'ACH Transfer'],
    ['WIRE_TRANSFER', 'Wire Transfer'],
    ['OTHER', 'Other'],
]
interface Props {
    onAddPayment: (payment: PaymentForm) => void
    invoice: InternalInvoice
    open: boolean
    onClose: () => void
}

interface NumberFormatCustomProps {
    inputRef: (instance: NumberFormat | null) => void
    onChange: (event: { target: { value: string } }) => void
}

export const RecordPaymentDialog: React.FC<Props> = ({
    onAddPayment,
    invoice,
    open,
    onClose,
}) => {
    const theme = useTheme()
    const [newPayment, setNewPayment] = useState<PaymentForm>({
        invoiceId: '',
        payment_amount: invoice.balance_due,
        payment_date: new Date().toISOString(),
        payment_method: '',
        payment_reference: '',
    })

    const handleAddPayment = () => {
        if (!invoice) return
        onAddPayment({
            ...newPayment,
            invoiceId: invoice.id.toString(),
        })
        setNewPayment({
            invoiceId: '',
            payment_amount: 0,
            payment_date: new Date().toISOString(),
            payment_method: '',
            payment_reference: '',
        })
        toast.success('Payment recorded successfully!')
        onClose()
    }

    const isPaymentValid = (invoice: InternalInvoice) => {
        if (!invoice) return false

        return (
            newPayment.payment_amount > 0 &&
            newPayment.payment_amount <= invoice.balance_due &&
            newPayment.payment_method &&
            newPayment.payment_date
        )
    }

    return (
        <Dialog
            open={open}
            onClose={onClose}
            fullWidth
            PaperProps={{
                style: {
                    borderRadius: 16,
                    position: 'absolute',
                    top: 90,
                    left: 160,
                    minWidth: 800,
                },
            }}
        >
            <Box
                style={{
                    padding: theme.spacing(3),
                }}
            >
                <Typography
                    style={{
                        ...typographyStyle(theme.typography.h6),
                        fontWeight: 'bold',
                        color: theme.palette.primary.main,
                        marginBottom: theme.spacing(2),
                    }}
                >
                    Record Payment
                </Typography>
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={3}>
                        <Box display="flex" alignItems="flex-start">
                            <TextField
                                fullWidth
                                label="Amount"
                                value={newPayment.payment_amount}
                                onChange={(e) =>
                                    setNewPayment({
                                        ...newPayment,
                                        payment_amount: parseFloat(
                                            e.target.value,
                                        ),
                                    })
                                }
                                InputProps={{
                                    inputComponent: NumberFormatCustom as any,
                                }}
                                error={
                                    newPayment.payment_amount >
                                    invoice.balance_due
                                }
                                helperText={
                                    newPayment.payment_amount >
                                    invoice.balance_due
                                        ? `Maximum amount is ${formatCurrency(
                                              invoice.balance_due,
                                          )}`
                                        : ''
                                }
                            />
                        </Box>
                    </Grid>
                    <Grid item xs={12} sm={3}>
                        <TextField
                            fullWidth
                            select
                            label="Payment Method"
                            value={newPayment.payment_method}
                            onChange={(e) =>
                                setNewPayment({
                                    ...newPayment,
                                    payment_method: e.target.value,
                                })
                            }
                        >
                            {PAYMENT_METHODS.map((method) => (
                                <MenuItem key={method[0]} value={method[0]}>
                                    {method[1]}
                                </MenuItem>
                            ))}
                        </TextField>
                    </Grid>
                    <Grid item xs={12} sm={3}>
                        <TextField
                            fullWidth
                            label="Reference"
                            value={newPayment.payment_reference}
                            onChange={(e) =>
                                setNewPayment({
                                    ...newPayment,
                                    payment_reference: e.target.value,
                                })
                            }
                            placeholder="Check #, Transaction ID, etc."
                        />
                    </Grid>
                    <Grid item xs={12} sm={3}>
                        <KeyboardDatePicker
                            fullWidth
                            label="Payment Date"
                            value={newPayment.payment_date}
                            onChange={(date) =>
                                setNewPayment({
                                    ...newPayment,
                                    payment_date:
                                        date && !isNaN(date.getTime())
                                            ? date.toISOString()
                                            : null,
                                })
                            }
                            format="MM/dd/yyyy"
                            autoOk
                        />
                    </Grid>
                </Grid>
                <DialogActions
                    style={{
                        display: 'flex',
                        justifyContent: 'flex-end',
                        marginTop: theme.spacing(2),
                    }}
                >
                    <Button
                        variant="contained"
                        color="secondary"
                        onClick={onClose}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleAddPayment}
                        disabled={!isPaymentValid(invoice)}
                    >
                        Record Payment
                    </Button>
                </DialogActions>
            </Box>
        </Dialog>
    )
}

function NumberFormatCustom(props: NumberFormatCustomProps) {
    const { inputRef, onChange, ...other } = props

    return (
        <NumberFormat
            {...other}
            getInputRef={inputRef}
            onValueChange={(values) => {
                onChange({
                    target: {
                        value: values.value,
                    },
                })
            }}
            thousandSeparator
            isNumericString
            prefix="$"
            decimalScale={2}
            fixedDecimalScale
        />
    )
}
